import { Component, computed, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { EAnalyticsStatus } from '@enums/campaign-analytics.enum';
import { EDeliverableType } from '@enums/campaign.enum';
import { EEventType, EEventViewMode } from '@enums/event-tracking.enum';
import { IAnalyticsNode } from '@interfaces/campaign-analytics.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { toEventFrontendTab } from '@utilities/event-payload-mapping.util';
import { map, startWith } from 'rxjs';
import { CampaignDetailsDeliverablesFilterSelectComponent } from '../campaign-details-deliverables/campaign-details-deliverables-filter-select/campaign-details-deliverables-filter-select.component';
import { CampaignDetailsDeliverablesGroupToggleComponent } from '../campaign-details-deliverables/campaign-details-deliverables-group-toggle/campaign-details-deliverables-group-toggle.component';
import { CampaignDetailsDeliverablesStatusFiltersComponent } from '../campaign-details-deliverables/campaign-details-deliverables-status-filters/campaign-details-deliverables-status-filters.component';
import { ECampaignDetailsTab, IAnalyticsFilterChangedEvent } from '../campaign-details.interface';
import { ICampaignExportConfig } from '../campaign-export-config/campaign-export-config.interface';
import { CampaignDetailsObservationsTableComponent } from './campaign-details-observations-table/campaign-details-observations-table.component';
import {
  ICampaignDetailsObservationsFilterState,
  ICampaignDetailsObservationsTableRow,
  TCampaignDetailsObservationsGroupedRow,
} from './campaign-details-observations.interface';
import { CampaignDetailsObservationsService } from './campaign-details-observations.service';

@Component({
  imports: [
    CampaignDetailsDeliverablesFilterSelectComponent,
    CampaignDetailsDeliverablesGroupToggleComponent,
    CampaignDetailsDeliverablesStatusFiltersComponent,
    CampaignDetailsObservationsTableComponent,
    TranslateModule,
  ],
  providers: [CampaignDetailsObservationsService],
  selector: 'app-campaign-details-observations',
  templateUrl: './campaign-details-observations.component.html',
})
export class CampaignDetailsObservationsComponent {
  private readonly observationsService = inject(CampaignDetailsObservationsService);
  private readonly translate = inject(TranslateService);
  private readonly eventTrackingService = inject(EventTrackingService);

  public readonly analyticsNodes = input.required<IAnalyticsNode[] | null>();
  public readonly campaignExportConfig = input.required<ICampaignExportConfig>();
  public readonly loading = input<boolean>(true);

  public readonly observationRowClicked = output<ICampaignDetailsObservationsTableRow>();
  public readonly analyticsFilterChanged = output<IAnalyticsFilterChangedEvent>();

  public readonly filterState = signal<ICampaignDetailsObservationsFilterState>(
    this.observationsService.createInitialFilterState(),
  );
  public readonly groupByContent = signal<boolean>(false);

  public readonly languageChange = toSignal(
    this.translate.onLangChange.pipe(
      map((event) => event.lang),
      startWith(this.translate.currentLang),
    ),
    { initialValue: this.translate.currentLang },
  );

  public readonly rows = computed((): ICampaignDetailsObservationsTableRow[] => {
    this.languageChange();

    return this.observationsService.buildRows(this.analyticsNodes());
  });

  public readonly filterOptions = computed(() => {
    this.languageChange();

    return this.observationsService.buildFilterOptions(this.rows(), this.translate);
  });

  public readonly observationTypeEmptyLabel = computed((): string => {
    this.languageChange();

    return this.translate.instant('pages.campaignDetails.observations.filters.allObservations', {
      number: this.filterOptions().observationTypes.length,
    }) as string;
  });

  public readonly isContentLoading = computed((): boolean => {
    const analyticsNodes = this.analyticsNodes();

    return this.loading() || analyticsNodes === null;
  });

  public readonly filteredRows = computed(() => this.observationsService.filterRows(this.rows(), this.filterState()));

  private readonly typeFilteredRows = computed(() =>
    this.observationsService.filterRows(this.rows(), {
      observationStatuses: new Set<EAnalyticsStatus>(),
      observationTypes: this.filterState().observationTypes,
    }),
  );

  public readonly statusOptions = computed(() =>
    this.observationsService.buildStatusOptions(this.typeFilteredRows(), this.filterState().observationStatuses),
  );

  public readonly visibleStatusOptions = computed(() => {
    const allowed = this.campaignExportConfig().allowedObservationStatuses;
    const options = this.statusOptions();

    if (allowed === null) {
      return options;
    }

    return options.filter((o) => allowed.includes(o.key as EAnalyticsStatus));
  });

  public readonly groupedRows = computed<TCampaignDetailsObservationsGroupedRow[]>(() =>
    this.observationsService.buildGroupedRows(this.filteredRows()),
  );

  public onObservationTypesChanged(values: Set<string>): void {
    const previousValues = this.filterState().observationTypes;

    this.filterState.update((currentState) => ({
      ...currentState,
      observationTypes: new Set(Array.from(values).map((value) => value as EDeliverableType)),
    }));

    this.emitFilterChanged('observation_types', previousValues, values);
  }

  public onObservationStatusesChanged(values: Set<EAnalyticsStatus>): void {
    const previousValues = this.filterState().observationStatuses;

    this.filterState.update((currentState) => ({
      ...currentState,
      observationStatuses: values,
    }));

    this.emitFilterChanged('observation_statuses', previousValues, values);
  }

  public onGroupByContentChanged(value: boolean): void {
    this.groupByContent.set(value);

    this.eventTrackingService.track(EEventType.campaignAnalyticsGroupByContentChanged, {
      currentTab: toEventFrontendTab(ECampaignDetailsTab.observations),
      newValue: String(value),
      previousValue: String(!value),
      viewMode: value ? EEventViewMode.groupedByContent : EEventViewMode.list,
    });
  }

  public onObservationRowClicked(row: ICampaignDetailsObservationsTableRow): void {
    this.observationRowClicked.emit(row);
  }

  private emitFilterChanged(currentFilter: string, previousValues: Set<unknown>, currentValues: Set<unknown>): void {
    this.analyticsFilterChanged.emit({
      currentFilter,
      currentFilterValue: Array.from(currentValues).join(','),
      previousFilterValue: Array.from(previousValues).join(','),
    });
  }
}
